<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useForm } from "@/form";
import { FormInput, FormTextarea, FormSwitch, FormLabel, FormErrors } from "@Components/Form";
import FormModal from "@Components/Modal/FormModal.vue";
import { PrimaryButton } from "@Components/Button";
import { MagicIcon } from "@Icons/index";
import PostCombobox from "./PostCombobox.vue";

const props = defineProps(["modelValue", "postTypes"]);
defineEmits(["udpate:modelValue"]);

const { t: __ } = useI18n();

const generateKeywordsModal = ref();
const generateKeywordsForm = useForm({
    keyword: "",
});

const generateKeywords = async () => {
    const keywords = await generateKeywordsForm.post("linking_manual_generate_keywords");

    if (props.modelValue.keywords.length > 0) {
        props.modelValue.keywords = props.modelValue.keywords + "\n" + keywords.join("\n");
    } else {
        props.modelValue.keywords = keywords.join("\n");
    }

    generateKeywordsModal.value.hide();
};
</script>

<template>
    <div class="space-y-6">
        <FormSwitch :z="1" :modelValue="modelValue.mode === 'custom'" @update:modelValue="($event) => (modelValue.mode = $event ? 'custom' : 'post')">{{
            __("Url personnalisée")
        }}</FormSwitch>
        <PostCombobox
            v-if="modelValue.mode === 'post'"
            :z="1"
            v-model="modelValue.post_id"
            :postTypes="postTypes"
            :initialPost="modelValue.initialPost"
            :errors="modelValue.getErrors('post_id')" />
        <FormInput
            v-if="modelValue.mode === 'custom'"
            v-model="modelValue.custom_url"
            :z="1"
            :label="__('Url')"
            :errors="modelValue.getErrors('custom_url')"
            :placeholder="__('Saisissez une url personnalisée')" />
        <div>
            <div class="flex justify-between items-center space-x-3">
                <FormLabel for="link-form-generate-keywords-keyword">
                    {{ __("Ancres") }}
                </FormLabel>
                <PrimaryButton :z="1" type="button" @click="generateKeywordsModal.show()" :icon="MagicIcon">
                    {{ __("Générer des ancres avec l'IA") }}
                </PrimaryButton>
            </div>
            <div class="mt-2">
                <FormTextarea
                    :z="1"
                    id="link-form-generate-keywords-keyword"
                    v-model="modelValue.keywords"
                    rows="8"
                    :errors="modelValue.getErrors('keywords')" />
            </div>
        </div>
        <FormInput
            type="number"
            :z="1"
            v-model.number="modelValue.max_links"
            :hint="__('0 pour l\'infini')"
            :help="__('Définissez le nombre maximum de liens ajoutés dans votre publication. 1 est recommandé.')"
            :label="__('Liens maximum')"
            :errors="modelValue.getErrors('max_links')" />
        <div>
            <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Types de posts") }}</div>
            <div class="mt-2">
                <div v-for="postType in postTypes" :key="postType.name" class="space-y-5">
                    <div class="relative flex items-start">
                        <div class="h-6 flex items-center">
                            <input
                                v-model="modelValue.post_types"
                                :id="'post-type-' + postType.name"
                                type="checkbox"
                                :value="postType.name"
                                class="w-4 h-4 text-gray-900 bg-white rounded border-gray-300 dark:text-gray-700 dark:border-gray-600 dark:focus:ring-gray-500 focus:ring-gray-900" />
                        </div>
                        <div class="ml-3 text-sm leading-6">
                            <label :for="'post-type-' + postType.name" class="font-medium text-gray-900 dark:text-white">{{ postType.label }}</label>
                        </div>
                    </div>
                </div>
            </div>
            <FormErrors v-if="modelValue.getErrors('post_types')" class="mt-2" :errors="modelValue.getErrors('post_types')" />
        </div>
        <div>
            <div class="text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Options du lien") }}</div>
            <div class="mt-2 space-y-3">
                <FormSwitch :z="1" v-model="modelValue.is_blank" :errors="modelValue.getErrors('is_blank')">{{ __("Blank") }}</FormSwitch>
                <FormSwitch :z="1" v-model="modelValue.is_follow" :errors="modelValue.getErrors('is_follow')">{{ __("Follow") }}</FormSwitch>
                <FormSwitch :z="1" v-model="modelValue.is_obfuscated" :errors="modelValue.getErrors('is_obfuscated')">{{ __("Obfusquer") }}</FormSwitch>
            </div>
        </div>

        <FormModal ref="generateKeywordsModal" @submit="generateKeywords" :title="__('Générer des ancres avec l\'IA')">
            <template #body>
                <div class="space-y-6">
                    <FormInput
                        :z="1"
                        v-model="generateKeywordsForm.keyword"
                        :label="__('Mot clé')"
                        :errors="generateKeywordsForm.getErrors('keyword')"
                        :placeholder="__('Saisissez un mot clé')" />
                </div>
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <PrimaryButton :processing="generateKeywordsForm.processing" type="submit" :z="1">
                        {{ __("Générer") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </div>
</template>
